import {LobbyEvent} from "../../api/ws/events";

export enum LobbyState {
    WAITING = 'WAITING',
    IN_GAME = 'IN_GAME',
}

export enum ReadyStatus {
    READY = 'READY',
    NOT_READY = 'NOT_READY',
}

export enum ConnectionStatus {
    ONLINE = 'ONLINE',
    HANG = 'HANG',
}

export interface Lobby {
    id: number,
    activeGameId?: number,
    gamesPlayed: number,
    version: number,
    title: string,
    capacity: number,
    users: LobbyUser[],
    state: LobbyState,
    created: Date
}

export interface LobbyCreate {
    title: string,
    password: string,
    capacity: number,
}

export interface LobbyUser {
    id: number;
    userId: number;
    lobbyId: number;
    user: User;
    lobbyUserData: LobbyUserData;
    joined: Date;
}

export interface User {
    id: number;
    username: string;
}

export interface LobbyUserData {
    readyStatus: ReadyStatus;
    gamesWon: number;
    currentScore: number;
    connectionStatus: ConnectionStatus;
}

export function reduceLobby(prev: Lobby | null, event: LobbyEvent): Lobby | null {
    if (event.type === 'LOBBY_STATE') {
        return {...event.lobby, version: event.version};
    }

    if (!prev) {
        return prev;
    }

    if (event.version <= prev.version) {
        console.warn('Ignore stale lobby event', event, 'currentVersion = ', prev.version);
        return prev;
    }

    const next: Lobby = {...prev, version: event.version};

    switch (event.type) {
        case 'PLAYER_JOINED': {
            const joined = event.player;

            const alreadyExists = next.users.some(u => u.id === joined.id);
            if (alreadyExists) {
                return next;
            }

            return {
                ...next,
                users: [...next.users, joined],
            };
        }

        case 'PLAYER_LEFT': {
            return {
                ...next,
                users: next.users.filter(u => u.userId !== event.playerId),
            };
        }

        case 'PLAYER_READY': {
            const {playerId, ready} = event;

            return {
                ...next,
                users: next.users.map(u =>
                    u.userId === playerId
                        ? {
                            ...u,
                            lobbyUserData: {
                                ...u.lobbyUserData,
                                readyStatus: ready
                            },
                        }
                        : u,
                ),
            };
        }

        case 'PLAYER_HANG': {
            const {playerId} = event;
            return {
                ...next,
                users: next.users.map(u =>
                    u.userId === playerId
                        ? {
                            ...u,
                            lobbyUserData: {
                                ...u.lobbyUserData,
                                connectionStatus: ConnectionStatus.HANG,
                            },
                        }
                        : u,
                ),
            };
        }

        case 'PLAYER_BACK': {
            const {playerId} = event;
            return {
                ...next,
                users: next.users.map(u =>
                    u.userId === playerId
                        ? {
                            ...u,
                            lobbyUserData: {
                                ...u.lobbyUserData,
                                connectionStatus: ConnectionStatus.ONLINE,
                            },
                        }
                        : u,
                ),
            };
        }

        case 'GAME_STARTED': {
            return {
                ...next,
                state: LobbyState.IN_GAME,
            };
        }

        default: {
            console.warn("Unknown event", event);
            return next;
        }
    }
}
